import React from 'react'

import FormatCurrency from 'components/Common/FormatCurrency'
import Pill from 'components/Luxkit/Pill'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import useDefaultAirport from 'hooks/useDefaultAirport'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import { connect } from 'react-redux'
import { buildSuggestedDatesParamsKey } from 'lib/search/searchUtils'
import { getSuggestedDates } from 'selectors/offerSelectors'
import { buildSuggestedDatesString } from 'lib/search/searchUtils'
import { prettyDates } from 'components/Search/utils'
import moment from 'moment'
import { calculateDiscount } from 'checkout/selectors/payment/generic'
import { pluralizeToString } from 'lib/string/pluralize'
import { getLowestPricePackage } from 'lib/offer/getLowestPricePackage'
import { useOptimizelyExperiment, OptimizelyClientSideExperiments } from 'lib/optimizely/optimizelyUtils'
import Group from 'components/utils/Group'
import styled from 'styled-components'

const TextGroup = styled(Group)`
  align-items: baseline;
  flex-wrap: wrap;
`

interface MappedStateProps {
  suggestedDates: App.OfferSuggestedDates;
}

export interface Props {
  offer: App.Offer | App.OfferSummary
  bestPricePackage?: App.Package
  filters?: App.OfferListFilters
  price: number
  duration: number
  showCompact?: boolean
}

const hideValueOfferTypes: Array<App.OfferType> = [OFFER_TYPE_LAST_MINUTE, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK]

function MapViewOfferTilePricing(props: Props & MappedStateProps) {
  const {
    offer,
    bestPricePackage,
    price,
    filters,
    duration,
    suggestedDates,
    showCompact = false,
  } = props

  const showNightlyPrice = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.searchNightlyPriceMapMarkers)

  const hasSuggestedCheckInCheckOut = suggestedDates?.checkIn && suggestedDates?.checkOut

  const defaultAirport = useDefaultAirport()
  const lowestPricePackage = getLowestPricePackage(offer, offer.packages, true)

  if (!bestPricePackage || !lowestPricePackage) {
    return null
  }

  const hasDates = !!filters?.checkIn
  const discountRate = calculateDiscount(lowestPricePackage.price, lowestPricePackage.value)

  const lowestPrice = lowestPricePackage.price

  const flightPrice = offer.flightPrices[defaultAirport?.code]

  let totalPrice = suggestedDates?.price ?? (hasDates || !showNightlyPrice) ? price : lowestPrice
  let totalValue = suggestedDates?.value ?? (hasDates || !showNightlyPrice) ? bestPricePackage.value : lowestPricePackage.value

  if (offer.bundledWithFlightsOnly) {
    totalPrice += flightPrice
    totalValue += flightPrice
  }

  const minimumDuration = offer.minDuration
  const nightlyPrice = duration ? totalPrice / (minimumDuration ?? 1) : totalPrice

  const showMinimumDuration = !hasDates && minimumDuration && minimumDuration > 1 && showNightlyPrice

  const showValue = !hideValueOfferTypes.includes(offer.type) && bestPricePackage.shouldDisplayValue && totalValue > totalPrice

  const { tileDurationLabel, bundledWithFlightsOnly } = offer

  const showFlightsOnlyText = bundledWithFlightsOnly && !showCompact

  let fromLabel = ''
  if (hasSuggestedCheckInCheckOut) {
    fromLabel = buildSuggestedDatesString(suggestedDates.checkIn, suggestedDates.checkOut)
  } else if (hasDates) {
    fromLabel = prettyDates(moment(filters.checkIn), moment(filters.checkOut))
    fromLabel += showFlightsOnlyText ? ' + flights from ' : ' from'
  } else if (tileDurationLabel && !showNightlyPrice) {
    fromLabel = tileDurationLabel.toLocaleLowerCase()
    fromLabel += showFlightsOnlyText ? ' + flights from ' : ' from'
  } else {
    fromLabel = showFlightsOnlyText ? 'Hotel + flights from ' : 'From'
  }

  const minimumStayCaption = `${pluralizeToString('night', minimumDuration ?? 0)} minimum • From `

  return (
    <>
      {!showCompact && <>
        {discountRate > 0 && <Pill kind="primary" variant="success">
          Save up to {discountRate}%
        </Pill>}
        <BodyText variant="small" align="end">
          {fromLabel}&nbsp;
          <BodyText variant="medium" weight="bold">
            <FormatCurrency value={(hasDates || !showNightlyPrice) ? totalPrice : nightlyPrice} format="roundedDollar"/>
          </BodyText> /{(hasDates || !showNightlyPrice) ? offer.saleUnit : 'night'}
        </BodyText>
        {showMinimumDuration && <Caption variant="medium">{minimumStayCaption}{<FormatCurrency value={totalPrice} />} total</Caption>}
      </>}
      {showCompact && <Group direction="vertical" gap={4}>
        <Group direction="horizontal" gap={4} fullWidth>
          <TextGroup direction="horizontal">
            <BodyText variant="medium">
              {fromLabel}&nbsp;
            </BodyText>
            <Group direction="horizontal" verticalAlign="center">
              <BodyText variant="large" weight="bold">
                <FormatCurrency value={(hasDates || !showNightlyPrice) ? totalPrice : nightlyPrice} format="roundedDollar"/>
              </BodyText>
              <BodyText variant="medium">
              &nbsp;/{(hasDates || !showNightlyPrice) ? 'total' : 'night'}
              </BodyText>
            </Group>
          </TextGroup>
        </Group>
        <div>
          {discountRate > 0 && !hasDates && <Pill kind="primary" variant="success">
            Save up to {discountRate}%
          </Pill>}
        </div>
        {showMinimumDuration && <Caption variant="medium">{minimumStayCaption}{<FormatCurrency value={totalPrice} />} total</Caption>}
        {hasDates && <TextGroup direction="horizontal" gap={4}>
          {showValue && <BodyText variant="medium" weight="bold" as="span">Valued up to <FormatCurrency value={totalValue} /></BodyText>}
          {discountRate > 0 && <Pill kind="primary" variant="success">
            Save up to {discountRate}%
          </Pill>}
        </TextGroup>}
      </Group>
      }
    </>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState, ownProps) => {
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(ownProps.filters?.flexibleMonths, ownProps.filters?.flexibleNights, ownProps.filters?.rooms)
  return {
    suggestedDates: getSuggestedDates(appState, flexibleSearchFilterKey, ownProps.offer.id),
  }
})(MapViewOfferTilePricing)
